/**
 * Admin-Postfach: Interessensanfragen von der öffentlichen Teaser-Seite
 * (Jun-2026 · User-Wunsch). Liste + Status-Wechsel (gelesen/kontaktiert/archiviert).
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, Mail, Phone, Building2, Inbox } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STATUS = [
    { key: "alle", label: "Alle" },
    { key: "neu", label: "Neu" },
    { key: "gelesen", label: "Gelesen" },
    { key: "kontaktiert", label: "Kontaktiert" },
    { key: "archiviert", label: "Archiviert" },
];
const NEXT = ["neu", "gelesen", "kontaktiert", "archiviert"];

export default function AdminContactLeadsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("alle");
    const [newCount, setNewCount] = useState(0);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/admin/contact-leads${filter !== "alle" ? `?status=${filter}` : ""}`)
            .then((r) => { setItems(r.data?.items || []); setNewCount(r.data?.new_count || 0); })
            .catch(() => toast.error("Anfragen konnten nicht geladen werden."))
            .finally(() => setLoading(false));
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (lead, status) => {
        try {
            await api.patch(`/admin/contact-leads/${lead.id}`, { status });
            toast.success(`Status: ${status}`);
            load();
        } catch { toast.error("Status konnte nicht geändert werden."); }
    };

    const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-contact-leads-page">
            <div className="flex items-center gap-3 mb-1">
                <Inbox className="w-6 h-6 text-[#2563EB]" />
                <h1 className="text-2xl font-extrabold tracking-tight">Interessensanfragen (Teaser-Seite)</h1>
                {newCount > 0 && <span className="text-xs font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{newCount} neu</span>}
            </div>
            <p className="text-sm text-gray-500 mb-5">Eingaben aus dem öffentlichen Kontaktformular. E-Mail-Benachrichtigung ist aktuell deaktiviert (Mock) — Anfragen erscheinen nur hier.</p>

            <div className="flex flex-wrap gap-2 mb-5">
                {STATUS.map((s) => (
                    <button key={s.key} onClick={() => setFilter(s.key)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-sm border ${filter === s.key ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}
                        data-testid={`contact-lead-filter-${s.key}`}>{s.label}</button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : items.length === 0 ? (
                <div className="py-16 text-center text-gray-500 text-sm border border-dashed border-gray-300 rounded-sm" data-testid="contact-leads-empty">Keine Anfragen in dieser Ansicht.</div>
            ) : (
                <div className="space-y-3">
                    {items.map((l) => (
                        <div key={l.id} className="border border-gray-200 rounded-sm p-4 bg-white" data-testid={`contact-lead-${l.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-[#111827]">{l.name}</span>
                                        {l.company && <span className="text-xs text-gray-500 flex items-center gap-1"><Building2 className="w-3 h-3" />{l.company}</span>}
                                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${l.status === "neu" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>{l.status}</span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-600">
                                        <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-[#2563EB]"><Mail className="w-3 h-3" />{l.email}</a>
                                        {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-[#2563EB]"><Phone className="w-3 h-3" />{l.phone}</a>}
                                    </div>
                                </div>
                                <span className="text-[11px] text-gray-400 shrink-0">{fmt(l.created_at)}</span>
                            </div>
                            <p className="mt-3 text-sm text-gray-700 whitespace-pre-line bg-gray-50 rounded-sm p-3">{l.message}</p>
                            <div className="flex gap-2 mt-3">
                                {NEXT.filter((s) => s !== l.status).map((s) => (
                                    <button key={s} onClick={() => setStatus(l, s)}
                                        className="text-[11px] font-bold px-2.5 py-1 rounded-sm border border-gray-300 text-gray-600 hover:border-[#2563EB] hover:text-[#2563EB]"
                                        data-testid={`contact-lead-set-${s}-${l.id}`}>{s}</button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
